import React, { useState } from 'react';
import { SlStatusChip, SlStatusChipColor } from '@stackline/ui';
import { Link } from 'react-router-dom';
import { Text } from '../Generic/Text';
import {
  Direction,
  getColorForMetricChangeValue,
  getIconForMetricChangeForDirection
} from 'src/components/BeaconV2Components/utils/chartStyles';
import { PRODUCT_CARD_HEIGHT, PRODUCT_CARD_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { BeaconProImageFallback } from 'src/components/SvgIcons';
import { SlFlippableCard } from 'src/components/BeaconV2Components/SlFlippableCard';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';
import Button from '@mui/material/Button';

export interface ProductCardProps {
  mainDisplayTitle: string;
  secondaryDisplayTitle: string;
  imageUri: string;
  mainMetric: string;
  secondaryMetric: string;
  availabilityStatusCode: string;
  direction: Direction;
  stacklineSku: string;
  retailerSku: string;
  categoryName: string;
  subCategoryName: string;
  statusColor: SlStatusChipColor;
  parentBrandName: string;
  productUrl: string;
  brandUrl: string;
  retailerUrl: string;
  categoryUrl: string;
  subcategoryUrl: string;
  showDirection?: boolean;
  showExcludeProductButton?: boolean;
  handleExcludeProduct?: (retailerSku: string) => void;
}

/**
 * Product image that will be rendered in product card, and if image is failed to load, this will render image fallback icon.
 */
export const ProductImage = ({
  imageUri,
  secondaryDisplayTitle,
  mainDisplayTitle,
  width = '106px',
  height = '106px',
  fallbackImageStyle = {},
  imageStyle
}: {
  imageUri: string;
  mainDisplayTitle: string;
  secondaryDisplayTitle: string;
  width?: number | string;
  height?: number | string;
  fallbackImageStyle?: React.CSSProperties;
  imageStyle?: React.CSSProperties;
}) => {
  const [imageError, setImageError] = useState(false);

  if (imageError) {
    return (
      <BeaconProImageFallback
        style={{
          width: '78.2px',
          height: '68.2px',
          objectFit: 'cover',
          marginTop: '12.5px',
          marginBottom: '21.5px',
          ...fallbackImageStyle
        }}
      />
    );
  }

  return (
    <img
      style={{
        height,
        maxWidth: width,
        objectFit: 'contain',
        ...imageStyle
      }}
      src={imageUri}
      alt={secondaryDisplayTitle || mainDisplayTitle}
      onError={() => setImageError(true)}
    />
  );
};

/**
 * Shows the details for a product card.
 */
export const ProductCardInner = ({
  availabilityStatusCode,
  showExcludeProductButton,
  handleExcludeProduct,
  imageUri,
  mainDisplayTitle,
  mainMetric,
  secondaryDisplayTitle,
  secondaryMetric,
  direction,
  stacklineSku,
  retailerSku,
  categoryName,
  subCategoryName,
  statusColor,
  parentBrandName,
  productUrl,
  brandUrl,
  retailerUrl,
  categoryUrl,
  subcategoryUrl,
  showDirection = true
}: ProductCardProps) => {
  const appName = getAppName();
  const fieldsByApp: {
    title: string;
    value: string;
    link?: string;
    newTab?: boolean;
  }[] = (() => {
    switch (appName) {
      case AppName.Beacon:
        return [
          {
            title: 'Product ID',
            value: stacklineSku,
            link: productUrl
          },
          {
            title: 'Retailer SKU',
            value: retailerSku,
            link: retailerUrl,
            newTab: true
          },
          {
            title: 'Status',
            value: availabilityStatusCode
          },
          {
            title: 'Brand',
            value: mainDisplayTitle,
            link: brandUrl
          },
          {
            title: 'Category',
            value: categoryName,
            link: categoryUrl
          },
          {
            title: 'Subcategory',
            value: subCategoryName,
            link: subcategoryUrl
          }
        ];
      case AppName.Atlas:
        return [
          {
            title: 'Product ID',
            value: stacklineSku,
            link: productUrl
          },
          {
            title: 'Retailer SKU',
            value: retailerSku,
            link: retailerUrl,
            newTab: true
          },
          {
            title: 'Brand',
            value: mainDisplayTitle,
            link: brandUrl
          },
          {
            title: 'Manufacturer',
            value: parentBrandName
          },
          {
            title: 'Category',
            value: categoryName,
            link: categoryUrl
          },
          {
            title: 'Subcategory',
            value: subCategoryName,
            link: subcategoryUrl
          }
        ];
      default:
        return [
          {
            title: 'Product ID',
            value: stacklineSku,
            link: productUrl
          },
          {
            title: 'Retailer SKU',
            value: retailerSku,
            link: retailerUrl,
            newTab: true
          },
          {
            title: 'Brand',
            value: mainDisplayTitle,
            link: brandUrl
          },
          {
            title: 'Category',
            value: categoryName,
            link: categoryUrl
          },
          {
            title: 'Subcategory',
            value: subCategoryName,
            link: subcategoryUrl
          }
        ];
    }
  })();

  const cardFront = ({ isHovered }: { isHovered: boolean }) => (
    <Flex flexDirection="column" gap="sm" sx={{ padding: '16px' }}>
      <Flex
        sx={{
          height: '26px'
        }}
      >
        {isHovered && showExcludeProductButton ? (
          <Button
            variant="text"
            sx={{ padding: 0, margin: 0, minWidth: 0, transform: 'translate(-2px, -2.5px)', textTransform: 'none' }}
            onClick={() => handleExcludeProduct(retailerSku)}
          >
            <SlStatusChip color="error">Exclude</SlStatusChip>
          </Button>
        ) : isHovered && availabilityStatusCode ? (
          <SlStatusChip color={statusColor}>{availabilityStatusCode}</SlStatusChip>
        ) : (
          <div></div>
        )}
      </Flex>
      <Flex flexDirection="column" gap="md">
        <Flex justifyContent="center">
          <Link to={productUrl}>
            <ProductImage
              imageUri={imageUri}
              mainDisplayTitle={mainDisplayTitle}
              secondaryDisplayTitle={secondaryDisplayTitle}
            />
          </Link>
        </Flex>
        <Link to={brandUrl}>
          <Text inline variant="subtitle2">
            {mainDisplayTitle}
          </Text>
        </Link>
      </Flex>
      <Flex alignItems="center" gap={6}>
        {/* This is an intentional deviation from the Typography system */}
        <Text variant="h4" sx={{ fontSize: 22, fontWeight: 500 }}>
          {mainMetric}
        </Text>
        <Flex alignItems="center" gap="xs">
          <div style={{ transform: 'translateY(1px)' }}>
            {showDirection ? getIconForMetricChangeForDirection(direction) : null}
          </div>
          <Text variant="subtitle3" color={getColorForMetricChangeValue(null, direction)}>
            {secondaryMetric}
          </Text>
        </Flex>
      </Flex>
      <Link to={productUrl}>
        <Text variant="body2" title={secondaryDisplayTitle} truncateLines={3}>
          {secondaryDisplayTitle}
        </Text>
      </Link>
    </Flex>
  );

  const cardBack = () => (
    <div>
      <Flex flexDirection="column" gap="sm" sx={{ padding: '24px 16px 16px' }}>
        {fieldsByApp.map(({ title, value, link, newTab }) => {
          const textValue = <Text variant="body3">{value}</Text>;
          const linkElement = newTab ? (
            <a href={link} target="_blank" rel="noreferrer" style={{ width: 'fit-content' }}>
              {textValue}
            </a>
          ) : link ? (
            <Link to={link} style={{ width: 'fit-content' }}>
              {textValue}
            </Link>
          ) : (
            textValue
          );
          return (
            <Flex flexDirection="column" key={`${title}${value}`} gap="xs">
              <Text transform="uppercase" variant="subtitle4">
                {title}
              </Text>
              {linkElement}
            </Flex>
          );
        })}
      </Flex>
    </div>
  );

  return (
    <SlFlippableCard
      flipIconSize="16px"
      sx={{ width: PRODUCT_CARD_WIDTH, height: PRODUCT_CARD_HEIGHT }}
      cardFront={cardFront}
      cardBack={cardBack}
    />
  );
};

export default ProductCardInner;
